import {
  ROAD_NEXT_SLOT,
  ROAD_THEMES,
  WORLD_NODES,
  isSlotNodeType,
  type BiomeId,
  type RealmSlot,
} from '@goodtime/shared';
import type { WorldSlotInfo } from '@goodtime/engine';
import { themeIdForNode } from '../../themes/slotTheme';

/**
 * What each of a world's slots shows on its world map's plate, from the
 * realm view's slots (goodtime-api: `GET /players/{id}/realm`):
 * - its stars;
 * - on a road slot whose road runs for the player, its chapter and the
 *   landmarks restored;
 * - on the slot a road's chapter opens (the world's second slot, and Castle
 *   Gardens for Royal Reels' chapter), that chapter, while the road runs.
 *
 * `roadOn` says whether a road slot's road runs for the player (the
 * `royal_road` flag and its slots).
 */
export function worldSlotInfo(
  world: number,
  slots: readonly RealmSlot[],
  roadOn: (themeId: string) => boolean,
): Map<number, WorldSlotInfo> {
  const byTheme = new Map(slots.map((s) => [s.themeId, s]));
  const out = new Map<number, WorldSlotInfo>();
  const nodes = WORLD_NODES.filter((n) => n.biome === world && isSlotNodeType(n.type));
  for (const node of nodes) {
    const row = byTheme.get(themeIdForNode(node));
    const info: WorldSlotInfo = {};
    if (row) info.stars = Math.max(0, Math.min(3, Math.floor(row.stars)));
    if (row && row.chapter !== undefined && row.restorable && roadOn(row.themeId)) {
      info.chapter = { n: row.chapter, restored: row.restored ?? 0, of: row.restorable };
    }
    out.set(node.id, info);
  }
  for (const [theme, next] of Object.entries(ROAD_NEXT_SLOT)) {
    const info = out.get(next.node);
    if (!info || !roadOn(theme)) continue;
    // The chapter's number: as the server sends it, or its place in the story.
    const chapter = byTheme.get(theme)?.chapter ?? ROAD_THEMES.indexOf(theme) + 1;
    if (chapter > 0) info.freeAfterChapter = chapter;
  }
  return out;
}

const SEEN_KEY = 'goodtime.worldMapOpen.';

/** The open slots a world's map showed last time on this device, or null
 *  when it has never shown here (no opening moment then). */
export function worldMapSeen(biome: BiomeId): number[] | null {
  try {
    const raw = localStorage.getItem(SEEN_KEY + biome);
    if (raw === null) return null;
    const ids: unknown = JSON.parse(raw);
    return Array.isArray(ids) ? ids.filter((n): n is number => typeof n === 'number') : null;
  } catch {
    return null;
  }
}

export function markWorldMapSeen(biome: BiomeId, ids: readonly number[]): void {
  try {
    localStorage.setItem(SEEN_KEY + biome, JSON.stringify(ids));
  } catch {
    // Storage off (private mode): the moment may show again next launch.
  }
}
